import React, { useState } from 'react';
import classNames from 'classnames';
// eslint-disable-next-line import/no-unresolved
import { Index, Configure, useInstantSearch } from 'react-instantsearch-next';

import {
  ALGOLIA_INDEX_NAME,
  SORT_BY_OPTIONS,
  ALGOLIA_PRODUCT_INDEXES,
} from 'commons/constants';
import FixedWidthContainer from 'components/consumer/FixedWidthContainer';
import ProductListingPageName from 'components/consumer/ProductListingPage/components/ProductListingPageName';
import DesktopShowHideFiltersButton from 'components/consumer/ProductListingPage/components/desktop/DesktopShowHideFiltersButton';
import MobileGridColumnToggle from 'components/consumer/ProductListingPage/components/mobile/MobileGridColumnToggle';
import DesktopProductListingSidebarContainer from 'components/consumer/ProductListingPage/components/desktop/DesktopProductListingSidebarContainer';
import useScreen from 'hooks/useScreen';
import ProductListingPageDescriptionUI from '../ProductListingPageDescriptionUI';
import AdvancedSearchSidebar from '../AdvancedSearchSidebar';
import AdvancedSearchProductHits from '../AdvancedSearchProductHits';
import useAdvancedSearchContext from '../../hooks/useAdvancedSearchContext';

import CustomerPhotos from '../../pages/CustomerPhotos';
import CustomerReviews from '../../pages/CustomerReviews';
import StaticPages from '../../pages/StaticPages';

import ProductListingSortBy from './components/ProductListingSortBy';
import MobileProductFilters from './components/MobileProductFilters';

// Ported from ProductListingPage

const AlgoliaHits = ({
  loading = false,
  pageName = '',
  variant = 'advanced-search',
  isFinalProductCountKnown = true,
  isDesktopSidebarOpen = true,
  pageLongDescription = '',
}) => {
  const { isMobile } = useScreen();
  const {
    currentSearchSection,
    currentProductIndex,
    isReadyToShipActive,
  } = useAdvancedSearchContext();
  const { results } = useInstantSearch();

  const [isSidebarOpen, setIsDesktopSidebarOpen] = useState(
    isDesktopSidebarOpen
  );
  const toggleSidebar = () => {
    setIsDesktopSidebarOpen(prevIsOpen => !prevIsOpen);
  };

  const searchTerm = results?.query || pageName || '';
  const displayText = searchTerm ? `"${searchTerm}"` : '""';

  const productsCount = results?.nbHits || 0;
  const resultsCountText =
    !loading && isFinalProductCountKnown && productsCount > 0
      ? `${productsCount} Results`
      : null;

  return (
    <FixedWidthContainer
      wide
      noMobilePadding
      noPadding={variant === 'advanced-search'}
    >
      <div
        className={classNames(
          'px-3 py-4 space-y-1',
          'md:px-[3.333vw] md:py-0 md:space-y-0 min-[1500px]:px-[50px]',
          'md:items-center md:bg-white md:flex md:h-[53px] md:sticky md:z-[11] md:gap-5 md:justify-between',
          'md:mt-4',
          {
            'md:top-[55px]': variant === 'advanced-search',
            'md:top-[117px]': !variant || variant !== 'advanced-search',
          }
        )}
      >
        <div className="md:flex md:gap-5 md:items-center">
          <div className="md:flex md:gap-2 md:items-center md:flex-nowrap">
            <ProductListingPageName
              pageName={
                <>
                  Results for {displayText}
                  {resultsCountText && (
                    <span className="text-sm text-gray-light2 font-normal ml-3">
                      {resultsCountText}
                    </span>
                  )}
                </>
              }
              showPageNameWhileLoading={variant === 'advanced-search'}
            />
          </div>

          <DesktopShowHideFiltersButton
            loading={loading}
            onClick={toggleSidebar}
          />
        </div>

        <div
          className={classNames(
            'flex flex-row justify-between gap-3 py-3',
            'md:block md:gap-0 md:py-0'
          )}
        >
          <div
            className={classNames(
              'flex-1 flex gap-1',
              'md:flex-initial md:block md:gap-0'
            )}
          >
            {ALGOLIA_PRODUCT_INDEXES.includes(currentSearchSection) &&
              isMobile && (
                <MobileProductFilters productsCount={productsCount} />
              )}

            <div className="flex-1 md:flex-initial md:flex md:flex-row md:gap-4 md:items-center">
              <ProductListingSortBy
                sortByOptions={SORT_BY_OPTIONS.filter(
                  option => option.value !== 'newest'
                )}
              />
            </div>
          </div>

          <MobileGridColumnToggle />
        </div>
      </div>

      <div className="md:flex md:flex-row md:gap md:mt-2 md:px-[3.333vw] min-[1500px]:px-[50px]">
        {!isMobile && (
          <DesktopProductListingSidebarContainer isOpen={isSidebarOpen}>
            <AdvancedSearchSidebar />
          </DesktopProductListingSidebarContainer>
        )}

        <div className="md:space-y-8 md:mb-8 md:flex-1">
          {/* search results */}
          {currentSearchSection === ALGOLIA_INDEX_NAME.PRODUCTS &&
            currentProductIndex === ALGOLIA_INDEX_NAME.PRODUCTS && (
              <AdvancedSearchProductHits />
            )}
          {/* search with non-default sort order order */}
          {currentSearchSection === ALGOLIA_INDEX_NAME.PRODUCTS &&
            currentProductIndex !== ALGOLIA_INDEX_NAME.PRODUCTS && (
              <Index indexName={currentProductIndex}>
                <Configure
                  facets={['*']}
                  facetFilters={isReadyToShipActive ? ['is_in_stock:true'] : []}
                  numericFilters={[
                    'item_availability<2',
                    'visibility>0',
                    'visibility<4',
                  ]}
                  attributesToRetrieve={[
                    '_highlightResult',
                    'product_id',
                    'name',
                    'is_in_stock',
                  ]}
                  clickAnalytics
                />
                <AdvancedSearchProductHits />
              </Index>
            )}

          {/* customer photos */}
          {currentSearchSection === ALGOLIA_INDEX_NAME.CUSTOMER_PHOTOS && (
            <Index indexName={ALGOLIA_INDEX_NAME.CUSTOMER_PHOTOS}>
              <CustomerPhotos />
            </Index>
          )}

          {/* customer reviews */}
          {currentSearchSection === ALGOLIA_INDEX_NAME.REVIEWS && (
            <Index indexName={ALGOLIA_INDEX_NAME.REVIEWS}>
              <CustomerReviews />
            </Index>
          )}

          {/* static pages */}
          {currentSearchSection === ALGOLIA_INDEX_NAME.STATIC_PAGES && (
            <Index indexName={ALGOLIA_INDEX_NAME.STATIC_PAGES}>
              <StaticPages />
            </Index>
          )}

          <ProductListingPageDescriptionUI
            pageLongDescription={pageLongDescription}
            pageName={pageName}
          />
        </div>
      </div>
    </FixedWidthContainer>
  );
};

export default AlgoliaHits;
